import { z } from 'zod';
import { futuresProductTermsSchema, futuresPriceChangeMoneyRaw } from './futuresReference.js';
const id = z.number().int().positive().max(4_294_967_295);
const money = z
    .string()
    .regex(/^(0|[1-9]\d{0,18})$/)
    .refine((v) => BigInt(v) < 9223372036854775807n);
const sourceUrl = z
    .url()
    .max(1024)
    .refine((v) => new URL(v).protocol === 'https:');
export const futuresResearchAssumptionInputSchema = z
    .object({
    productId: id,
    code: z.string().regex(/^[a-z0-9][a-z0-9-]{0,79}$/),
    authoredTimestamp: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    settlementCurrency: z.string().regex(/^[A-Z]{3}$/),
    feePerContractSideRaw: money,
    slippageTicksPerSide: z.number().int().nonnegative().max(4_294_967_295),
    capitalAllocationPerContractRaw: money.refine((v) => BigInt(v) > 0),
    brokerFeeSourceUrl: sourceUrl,
    exchangeFeeSourceUrl: sourceUrl,
    feeRationale: z.string().trim().min(1).max(512),
    capitalRationale: z.string().trim().min(1).max(512)
})
    .strict();
export const futuresResearchAssumptionSchema = futuresResearchAssumptionInputSchema.extend({ id });
/** No timestamp lookup/default: the run must freeze the explicitly selected immutable scenario identity. */
export function estimateFuturesResearchFill(input) {
    const terms = futuresProductTermsSchema.parse(input.terms);
    const assumption = futuresResearchAssumptionSchema.parse(input.assumption);
    const contracts = id.parse(input.contracts);
    if (assumption.productId !== terms.productId || assumption.settlementCurrency !== terms.settlementCurrency)
        throw new Error('Futures research scenario does not match the product and settlement currency');
    if (input.transactionSide !== 'buy' && input.transactionSide !== 'sell')
        throw new Error('Invalid futures transaction side');
    // Validate source prices/ticks through the existing exact monetary boundary.
    futuresPriceChangeMoneyRaw({
        terms,
        fromPriceRaw: input.referencePriceRaw,
        toPriceRaw: input.referencePriceRaw,
        contracts,
        positionSide: 'long'
    });
    const offset = BigInt(terms.tickSizeRaw) * BigInt(assumption.slippageTicksPerSide);
    const modeledPriceRaw = (BigInt(input.referencePriceRaw) + (input.transactionSide === 'buy' ? offset : -offset)).toString();
    const slippageMoneyRaw = futuresPriceChangeMoneyRaw({
        terms,
        fromPriceRaw: input.referencePriceRaw,
        toPriceRaw: modeledPriceRaw,
        contracts,
        positionSide: input.transactionSide === 'buy' ? 'long' : 'short'
    });
    return {
        modeledPriceRaw,
        feeMoneyRaw: money.parse((BigInt(assumption.feePerContractSideRaw) * BigInt(contracts)).toString()),
        slippageMoneyRaw: money.parse(slippageMoneyRaw)
    };
}
/** Reserve authored research capital separately from cash P&L and fees; this is not a buying-power check. */
export function futuresResearchCapitalRaw(assumption, contracts) {
    const validated = futuresResearchAssumptionSchema.parse(assumption);
    return money.parse((BigInt(validated.capitalAllocationPerContractRaw) * BigInt(id.parse(contracts))).toString());
}
//# sourceMappingURL=futuresResearch.js.map